import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiScissors, FiInfo } from 'react-icons/fi';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';
import ProcessingLoader from '../components/ProcessingLoader';
import ResultDisplay from '../components/ResultDisplay';
import './ToolPage.css';

const BackgroundRemoval = () => {
  const [file, setFile] = useState(null);
  const [originalPreview, setOriginalPreview] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setOriginalPreview(selectedFile ? URL.createObjectURL(selectedFile) : null);
    setProcessedImage(null);
    setError(null);
  };

  const handleProcess = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/background-removal', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });

      const imageUrl = URL.createObjectURL(response.data);
      setProcessedImage(imageUrl);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!processedImage) return;
    
    const link = document.createElement('a');
    link.href = processedImage;
    link.download = `background-removed-${Date.now()}.png`;
    link.click();
  };

  const handleReset = () => {
    setFile(null);
    setOriginalPreview(null);
    setProcessedImage(null);
    setError(null);
  };

  return (
    <div className="tool-page">
      {isProcessing && (
        <ProcessingLoader 
          message="Removing background..."
          subMessage="Our AI is separating the subject from the background"
        />
      )}

      <div className="tool-hero">
        <div className="container">
          <motion.div 
            className="tool-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="tool-icon gradient-secondary">
              <FiScissors />
            </div>
            <h1>Background Removal</h1>
            <p>Instantly remove backgrounds from any image. Perfect for product photos, 
               portraits, and creative projects.</p>
          </motion.div>
        </div>
      </div>

      <div className="tool-content">
        <div className="container">
          {!processedImage ? (
            <motion.div 
              className="tool-upload-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ImageUploader 
                onFileSelect={handleFileSelect}
                title="Upload image to remove background"
                subtitle="Best results with clear subjects"
              />

              {file && (
                <motion.div 
                  className="process-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <button 
                    className="btn-process"
                    onClick={handleProcess}
                    disabled={isProcessing}
                  >
                    Remove Background
                  </button>
                </motion.div>
              )}

              {error && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <ResultDisplay
              originalImage={originalPreview}
              processedImage={processedImage}
              onDownload={handleDownload}
              onReset={handleReset}
            />
          )}

          <div className="tool-info-section">
            <div className="info-card">
              <FiInfo className="info-icon" />
              <h3>How it works</h3>
              <p>Our AI uses advanced segmentation technology to detect and separate the subject 
                 from the background. The result is a transparent PNG that you can use anywhere.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundRemoval;
